import { Injectable, inject } from '@angular/core';
import { ApiUrl, IrisApiClient, IrisQueryParams } from '@iris/api-query';
import { IrisDocument } from '@iris/common/models/IrisDocument';
import { Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { isNeverFilterQueryParams } from '@iris/common/modules/dynamic-forms/df-filter/df-filter.utils';
import { omitBy } from 'lodash';
import type { IrisDocumentTemplateI } from '@iris/common/models/IrisDocumentTemplate';
import { shareReplay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
@ApiUrl('{iris@formsUrlPart}/documents/v2/document-templates')
export class IrisDocumentByTemplateV2Service extends IrisApiClient<IrisDocument> {
  private readonly itemCache = new Map<string, Observable<IrisDocument>>();

  constructor() {
    const httpClient = inject(HttpClient);

    super(httpClient);
  }

  getByIdCached(documentTemplateId: IrisDocumentTemplateI['id'], documentId: IrisDocument['documentId'], force = false): Observable<IrisDocument> {
    const cacheKey = `${documentTemplateId}__${documentId}`;
    if (!this.itemCache.has(cacheKey) || force) {
      this.itemCache.set(cacheKey, this.getDocument(documentTemplateId, documentId).pipe(
        shareReplay({ bufferSize: 1, refCount: false }),
      ));
    }
    return this.itemCache.get(cacheKey);
  }

  @ApiUrl('~/{documentTemplateId}/documents/{documentId}/{direction}-document')
  getSiblingDocument<T = IrisDocument>(documentTemplateId: number, documentId: string, direction: 'prev' | 'next', params: IrisQueryParams = new IrisQueryParams()): Observable<T> {
    return this.httpClient.get<T>(this.url({ documentTemplateId, documentId, direction }), { params: params.toObject() });
  }

  @ApiUrl('~/{documentTemplateId}/documents/{documentId}')
  getDocument<T = IrisDocument>(documentTemplateId: number, documentId: string): Observable<T> {
    return this.httpClient.get<T>(this.url({ documentTemplateId, documentId }));
  }

  @ApiUrl('~/{templateId}/documents')
  getDocuments<T = IrisDocument>(documentTemplateId: number, params: IrisQueryParams = new IrisQueryParams()): Observable<T[]> {
    if (isNeverFilterQueryParams(params)) { return of<T[]>([]); }
    return this.httpClient.get<T[]>(this.url({ templateId: documentTemplateId }), { params: params.toObject() });
  }

  @ApiUrl('~/{documentTemplateId}/documents/get-with-post-filter')
  getDocumentsByTemplateIdWithPostFilter<T = IrisDocument>(documentTemplateId: number | string, params: IrisQueryParams = new IrisQueryParams()): Observable<T[]> {
    if (isNeverFilterQueryParams(params)) { return of<T[]>([]); }
    return this.httpClient.post<T[]>(this.url({ documentTemplateId }), omitBy({
      filter: params.filter?.map(x => x.toObject()),
      onlyFields: params.onlyFields,
      excludeFields: params.excludeFields,
      limit: params.limit,
      offset: params.offset,
      orderBy: params.orderBy?.map(x => x.toObject()),
    }, x => x == null));
  }

  @ApiUrl('~/{documentTemplateId}/documents')
  saveDocument(documentTemplateId: number, document: IrisDocument): Observable<IrisDocument> {
    return this.httpClient.post<IrisDocument>(this.url({ documentTemplateId }), document);
  }

  @ApiUrl('~/{documentTemplateId}/documents/{documentId}')
  updateDocument(documentTemplateId: number, documentId: string, document: IrisDocument): Observable<IrisDocument> {
    return this.httpClient.post<IrisDocument>(this.url({ documentTemplateId, documentId }), document);
  }

  @ApiUrl('~/{documentTemplateId}/document/{documentId}')
  deleteDocument(documentTemplateId: number, documentId: string): Observable<void> {
    return this.httpClient.delete<void>(this.url({ documentTemplateId, documentId }));
  }

  @ApiUrl('~/{documentTemplateId}/documents/bulk-remove')
  bulkRemove(documentTemplateId: number, documentIds: string[]): Observable<IrisDocument[]> {
    return this.httpClient.post<IrisDocument[]>(this.url({ documentTemplateId }), documentIds);
  }

  @ApiUrl('~/{documentTemplateId}/documents/{documentId}/for-printing')
  getForPrinting(documentTemplateId: number, documentId: string): Observable<IrisDocument> {
    return this.httpClient.get<IrisDocument>(this.url({ documentTemplateId, documentId }));
  }

  @ApiUrl('~/{documentTemplateId}/documents/{documentId}/partly')
  updateDocumentPartly(documentTemplateId: number, documentId: string, partialData: unknown): Observable<IrisDocument> {
    return this.httpClient.post<IrisDocument>(this.url({ documentTemplateId, documentId }), partialData);
  }
}
